import './bookmark.scss';
import React, { useRef, useState } from 'react';
import { IToolPanel } from 'ag-grid-community';
import Sheet from 'atoms/sheet/sheet';
import SheetBody from 'atoms/sheet/sheetBody/sheetBody';
import Heading from 'atoms/heading/heading';
import Stack from 'molecules/stack/stack';
import StackItem from 'molecules/stackItem/stackItem';
import { Button, List, Tooltip } from 'antd';
import { getDispatch, getState } from 'reducers';
import { applyBookmark, deleteBookmark, saveBookmark } from 'models/bookmarks/sagaActions';
import { useSelector } from 'react-redux';
import { getUserGridBookmarks } from 'models/bookmarks/selectors';
import { BookmarkDialog } from './bookMarkDialog';
import ThetaMenu from 'organisms/thetaMenu/thetaMenu';
import { DROPDOWN_MENU_BOOKMARK, DROPDOWN_VALUES } from './constants';
import { isLoadingEnabled } from 'models/loaders/selectors';
import Loader from 'atoms/loader';
import Icon from 'atoms/icons/icon';
import { usePopupParent } from 'hooks/usePopupParent';
import * as tableColumnSagaActions from 'models/tableColumns/sagaActions';

// --- Types ---
interface CustomToolPanelProps extends IToolPanel {
	tableId: string;
}

export interface Bookmark {
	BookmarkID: string;
	BookmarkName: string;
}

interface BookmarkDialogConfig {
	isEnabled: boolean;
	bookmark: Bookmark | null;
}

// infer DropdownValue type from constants
type DropdownValue = (typeof DROPDOWN_VALUES)[keyof typeof DROPDOWN_VALUES];

// --- Component ---
export const Bookmarks: React.FC<CustomToolPanelProps> = (props) => {
	const { tableId } = props;
	const getPopupParent = usePopupParent('full-screen');
	const gridRef = useRef<any>(null);

	const [bookmarkDialogConfig, setBookmarkDialog] = useState<BookmarkDialogConfig>({
		isEnabled: false,
		bookmark: null,
	});

	const gridBookmarks: Bookmark[] = useSelector((state: any) => getUserGridBookmarks(state, tableId));

	const isLoadingBookmarks: boolean = useSelector((state: any) => isLoadingEnabled(state, 'bookmarks'));

	const dispatch = getDispatch();

	const handleApplyBookmark = async (bookmarkId: string) => {
		dispatch(
			applyBookmark({
				bookmarkId,
				tableId,
				onSuccess: () => {
					dispatch(tableColumnSagaActions.fetchTableColumnsAg(Number(tableId), { forced: true }));
				},
			})
		);
	};

	const handleMenuItemClick = (value: DropdownValue, bookmark: Bookmark): void => {
		switch (value) {
			case DROPDOWN_VALUES.UPDATE:
				dispatch(
					saveBookmark({
						tableId,
						bookmarkId: bookmark.BookmarkID,
						bookmarkName: bookmark.BookmarkName,
					})
				);
				break;
			case DROPDOWN_VALUES.RENAME:
				setBookmarkDialog({
					isEnabled: true,
					bookmark,
				});
				break;
			case DROPDOWN_VALUES.DELETE:
				dispatch(deleteBookmark({ bookmarkId: bookmark.BookmarkID, tableId }));
				break;
		}
	};

	return (
		<>
			{bookmarkDialogConfig.isEnabled && (
				<BookmarkDialog
					onHide={() => setBookmarkDialog({ isEnabled: false, bookmark: null })}
					selectedBookmark={bookmarkDialogConfig.bookmark}
					tableId={tableId}
				/>
			)}

			<Sheet>
				<SheetBody isTransparent>
					<Stack isVertical gutter="small">
						<StackItem>
							<Stack className="px-2 py-3">
								<StackItem isGrow>
									<Heading type="primary">Bookmarks</Heading>
								</StackItem>
								<StackItem>
									<Button
										type="primary"
										onClick={() => setBookmarkDialog({ isEnabled: true, bookmark: null })}
										icon={<Icon isPadded={false} faAlias="faPlus" size="tiny" />}
									>
										Add
									</Button>
								</StackItem>
							</Stack>
						</StackItem>

						{isLoadingBookmarks ? (
							<StackItem>
								<Loader size="small" />
							</StackItem>
						) : (
							<StackItem isVertical isGrow>
								<List<Bookmark>
									className="w-100"
									itemLayout="horizontal"
									dataSource={gridBookmarks}
									renderItem={({ BookmarkName, BookmarkID }) => (
										<List.Item
											className="bookmarkLists"
											key={BookmarkID}
											actions={[
												<ThetaMenu
													key="menu"
													type="3DotsVertical"
													options={DROPDOWN_MENU_BOOKMARK}
													handleItemClick={(value: DropdownValue) =>
														handleMenuItemClick(value, {
															BookmarkName,
															BookmarkID,
														})
													}
													isDisabled={() => false}
												/>,
											]}
										>
											<Tooltip title={BookmarkName} getPopupContainer={getPopupParent}>
												<span
													className="bookmarkNameEllipsis"
													role="button"
													tabIndex={0}
													onClick={() => handleApplyBookmark(BookmarkID)}
													onKeyDown={(e) =>
														(e.key === 'Enter' || e.key === ' ') &&
														handleApplyBookmark(BookmarkID)
													}
												>
													{BookmarkName}
												</span>
											</Tooltip>
										</List.Item>
									)}
								/>
							</StackItem>
						)}
					</Stack>
				</SheetBody>
			</Sheet>
		</>
	);
};
